<template>
  <section class="table-sampler-classification-noise">
    <d-form-row class="m-0 mb-1">
      <d-col cols="3" v-if="currentCriterion === 'AREN/VDVR'">
      <label>VDVR</label>
      <select
        class="form-control"
        v-model="model.vdvr_object"
        @change="applyClassification"
        required
      >
        <option
          v-for="option in vdvrOptions"
          :key="option.label"
          :value="option"
        >
          {{ option.label }}
        </option>
      </select>
    </d-col>
      <d-col cols="auto">
        <label>AREN</label>
        <select
          class="form-control"
          v-model="model.aren_object"
          @change="applyClassification"
          required
        >
          <option
            v-for="option in arenOptions"
            :key="option.label"
            :value="option"
          >
            {{ option.label }}
          </option>
        </select>
      </d-col>
      <d-col cols="auto">
        <label>Considerações</label>
        <select
          class="form-control"
          v-model="model.consideration"
          @change="handleManualConsideration"
          required
        >
          <option
            v-for="option in considerationOptions"
            :key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>
      </d-col>
      <d-col cols>
        <label>Recomendação</label>
        <select
          class="form-control"
          v-model="model.recomendation"
          required
        >
          <option
            v-for="option in recommendationOptions"
            :key="option"
            :value="option"
          >
            {{ option }}
          </option>
        </select>
      </d-col>
    </d-form-row>
    <d-form-row class="m-0 mb-1">
      <d-col cols="12">
        <label>Recomendação da exposição</label>
        <textarea
          type="text"
          class="form-control"
          v-model="model.exposition_recomendation"
          required
        />
      </d-col>
    </d-form-row>
  </section>
</template>

<script>
import { mapActions } from 'vuex';

import Events from '_utils/events';
import { cloneDeep } from '_utils';
import { NOISE_CLASSIFICATION, _LOAD_MODEL_, _UPDATE_SAMPLE_CRITERION_VIBRATION_ } from '../../SampleModel';

export default {
  name: 'Sample-Classification-Vibration',
  props: {
    classification: {
      type: Object,
      default: null,
    },
  },
  computed: {
    levels() {
      const classificationSettings = this.$store.state.settings.riskClassifications?.vibration || null;
      const currentNorm = this.currentCriterion === 'AREN/VDVR' ? 'nho10' : 'nho09';

      return classificationSettings?.[currentNorm]?.levels || {};
    },
    arenOptions() {
      return Object.entries(this.levels).map(([levelKey, level]) => {
        const min = level.min?.AREN;
        const max = level.max?.AREN;

        if (min !== undefined && max !== undefined) {
          return {
            value: `${min}a${max}`,
            label: `${min} a ${max}`,
            levelKey,
          };
        }

        return {
          value: `acima-${min}`,
          label: `acima de ${min}`,
          levelKey,
        };
      });
    },
    vdvrOptions() {
      return Object.entries(this.levels).map(([levelKey, level]) => {
        const min = level.min?.VDVR;
        const max = level.max?.VDVR;

        if (max !== undefined) {
          return {
            value: `${min}a${max}`,
            label: `${min} a ${max}`,
            levelKey,
          };
        }

        return {
          value: `acima-${min}`,
          label: `acima de ${min}`,
          levelKey,
        };
      });
    },
    considerationOptions() {
      if (!this.levels) return [];

      const values = Object.values(this.levels)
        .map(level => level.technicalConsideration)
        .filter(Boolean);

      return [...new Set(values)];
    },
    recommendationOptions() {
      if (!this.levels) return [];

      const values = Object.values(this.levels)
        .map(level => level.recommendedAction)
        .filter(Boolean);

      return [...new Set(values)];
    },
  },
  data() {
    return {
      model: cloneDeep(NOISE_CLASSIFICATION),
      currentCriterion: null,
    };
  },
  methods: {
    ...mapActions(['getSettings']),
    applyClassification() {
      this.model.aren = this.model.aren_object?.label || null;
      this.model.vdvr = this.model.vdvr_object?.label || null;

      if (this.currentCriterion === 'AREN/VDVR') {
        const arenKey = this.model.aren_object?.levelKey;
        const vdvrKey = this.model.vdvr_object?.levelKey;

        if (arenKey !== vdvrKey) {
          this.clearClassification();
          return;
        }

        const level = this.levels[arenKey];
        if (!level) {
          this.clearClassification();
          return;
        }

        this.model.consideration = level.technicalConsideration;
        this.model.recomendation = level.recommendedAction;
        return;
      }

      const arenKey = this.model.aren_object?.levelKey;
      if (!arenKey) return;

      const level = this.levels[arenKey];
      if (!level) return;

      this.model.consideration = level.technicalConsideration;
      this.model.recomendation = level.recommendedAction;
    },
    handleManualConsideration() {
      const levelMatch = Object.values(this.levels).find(level => level.technicalConsideration === this.model.consideration);

      if (levelMatch) {
        this.model.recomendation = levelMatch.recommendedAction;
      }
    },
    clearClassification() {
      this.model.consideration = null;
      this.model.recomendation = null;
    },
    setModel() {
      if (this.classification) {
        this.model = this.classification;
      }
    },
    handleCriterionChange(agent) {
      this.currentCriterion = agent?.criterion || null;
      this.clearClassification();

      if (this.currentCriterion !== 'AREN/VDVR') {
        this.model.vdvr = null;
        this.model.vdvr_object = null;
      }
    },
  },
  mounted() {
    this.getSettings().then(() => this.setModel());
    this.setModel();
    const { sample } = this.$store.state.sample;
    this.currentCriterion = sample?.memory_field?.criterion || null;
    Events.$on(_LOAD_MODEL_, () => {
      this.setModel();
    });
    Events.$on(_UPDATE_SAMPLE_CRITERION_VIBRATION_, this.handleCriterionChange);
  },
  watch: {
    model: {
      handler(value) {
        const payload = cloneDeep(value);

        if (this.currentCriterion !== 'AREN/VDVR') {
          delete payload.vdvr;
          delete payload.vdvr_object;
        }

        this.$emit('update:classification', payload);
      },
      deep: true,
    },
  },
};
</script>

